import { useEffect, useState } from 'react'
import { MapPin, Pencil, Tag, X } from 'lucide-react'
import { createProduct, getLocations, saveProductDiscount } from '../../api/master'
import { addPickLocation, deletePickLocation, getAreas, listPickLocations } from '../../api/inventory'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import { Field } from './masterShared'

const num = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback)

/** Bins at a location where items can be picked from (bins first). */
export async function binsAt(locationId) {
  if (!locationId) return []
  const rows = await getAreas(locationId)
  const bins = (rows || []).filter((a) => String(a.level || '').toLowerCase() === 'bin')
  return bins.length ? bins : (rows || [])
}

/** The locations the user may pick from, and the bins at the chosen one. */
export function useBinChoice() {
  const [locations, setLocations] = useState([])
  const [locationId, setLocationId] = useState('')
  const [bins, setBins] = useState([])
  useEffect(() => {
    getLocations().then((rows) => {
      const list = Array.isArray(rows) ? rows : []
      setLocations(list)
      const store = list.find((l) => String(l.location_type || '').toLowerCase() === 'store') || list[0]
      if (store) setLocationId(String(store.location_id))
    }).catch(() => setLocations([]))
  }, [])
  useEffect(() => {
    setBins([])
    binsAt(locationId).then(setBins).catch(() => setBins([]))
  }, [locationId])
  return { locations, locationId, setLocationId, bins }
}

/** Bar shown while items are ticked, with what can be changed for all of them. */
export default function ItemBulkActions({ selected, binTracking, onClear, onDone }) {
  const [open, setOpen] = useState('')
  if (!selected.length) return null
  return (
    <>
      <div className="mf-bulk-bar" role="region" aria-label="Selected products and services">
        <span className="mf-bulk-count">{selected.length} selected</span>
        <button type="button" onClick={() => setOpen('discount')}><Tag size={13} />Discount</button>
        {binTracking && <button type="button" onClick={() => setOpen('bin')}><MapPin size={13} />Pick bin</button>}
        <button type="button" onClick={() => setOpen('edit')}><Pencil size={13} />Price and tax</button>
        <button type="button" className="mf-bulk-clear" onClick={onClear} aria-label="Clear selection"><X size={14} /></button>
      </div>
      {open === 'discount' && <BulkDiscount selected={selected} onClose={() => setOpen('')} onDone={onDone} />}
      {open === 'bin' && <BulkPickBin selected={selected} onClose={() => setOpen('')} onDone={onDone} />}
      {open === 'edit' && <BulkEdit selected={selected} onClose={() => setOpen('')} onDone={onDone} />}
    </>
  )
}

/** Runs fn for each item; returns how many failed. */
async function forEach(items, fn) {
  let failed = 0
  for (const item of items) {
    try { await fn(item) } catch { failed += 1 }
  }
  return failed
}

function BulkDiscount({ selected, onClose, onDone }) {
  const [type, setType] = useState('percent')
  const [value, setValue] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const apply = async () => {
    const v = num(value, NaN)
    if (type !== 'none' && (!Number.isFinite(v) || v < 0 || (type === 'percent' && v > 100))) {
      setError(type === 'percent' ? 'Enter a percent between 0 and 100.' : 'Enter an amount of 0 or more.')
      return
    }
    setWorking(true)
    setError('')
    const failed = await forEach(selected, (item) => saveProductDiscount({ product_id: item.product_id, discount_type: type, discount_value: type === 'none' ? 0 : v }))
    setWorking(false)
    if (failed) { setError(`${failed} of ${selected.length} weren’t updated.`); return }
    onDone(type === 'none' ? 'Discounts removed' : 'Discount set')
    onClose()
  }
  return (
    <Modal open onClose={onClose} title="Discount" subtitle={`For ${selected.length} selected`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button><Button variant="primary" onClick={apply} loading={working}>Apply</Button></>}>
      {error && <ErrorBanner message={error} />}
      <div className="mf-modal-grid">
        <Field label="Discount">
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="percent">Percent off</option>
            <option value="amount">Amount off</option>
            <option value="none">No discount</option>
          </select>
        </Field>
        {type !== 'none' && (
          <Field label={type === 'percent' ? 'Percent' : 'Amount'}>
            <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" placeholder={type === 'percent' ? '10' : '0.00'} />
          </Field>
        )}
      </div>
    </Modal>
  )
}

function BulkPickBin({ selected, onClose, onDone }) {
  const { locations, locationId, setLocationId, bins } = useBinChoice()
  const [action, setAction] = useState('replace')
  const [areaId, setAreaId] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const stock = selected.filter((i) => Number(i.is_service || 0) !== 1)
  const apply = async () => {
    const area = Number(areaId)
    if (action !== 'clear' && !area) { setError('Choose a bin.'); return }
    setWorking(true)
    setError('')
    const failed = await forEach(stock, async (item) => {
      const existing = await listPickLocations([item.product_id])
      if (action !== 'add') {
        for (const pick of existing || []) await deletePickLocation({ product_id: item.product_id, area_id: pick.area_id })
      }
      if (action === 'replace') await addPickLocation({ product_id: item.product_id, area_id: area, is_primary: true })
      if (action === 'add' && !(existing || []).some((p) => Number(p.area_id) === area)) {
        await addPickLocation({ product_id: item.product_id, area_id: area, is_primary: !(existing || []).some((p) => Number(p.is_primary) === 1) })
      }
    })
    setWorking(false)
    if (failed) { setError(`${failed} of ${stock.length} weren’t updated.`); return }
    onDone(action === 'clear' ? 'Pick bins cleared' : 'Pick bin set')
    onClose()
  }
  return (
    <Modal open onClose={onClose} title="Pick bin" subtitle={`For ${stock.length} ${stock.length === 1 ? 'product' : 'products'}${stock.length < selected.length ? ' (services skipped)' : ''}`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button><Button variant="primary" onClick={apply} loading={working}>Apply</Button></>}>
      {error && <ErrorBanner message={error} />}
      <div className="mf-modal-grid">
        <Field label="Change">
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="replace">Use only this bin</option>
            <option value="add">Add this bin</option>
            <option value="clear">Remove all pick bins</option>
          </select>
        </Field>
        {action !== 'clear' && (
          <>
            <Field label="Location">
              <select value={locationId} onChange={(e) => { setLocationId(e.target.value); setAreaId('') }}>
                {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Bin">
              <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                <option value="">{bins.length ? 'Choose a bin…' : 'No bins at this location'}</option>
                {bins.map((b) => <option key={b.area_id} value={b.area_id}>{b.path || b.name || b.area_id}</option>)}
              </select>
            </Field>
          </>
        )}
      </div>
    </Modal>
  )
}

function Check({ state, set, label }) {
  return <label className="ci-form-toggle mf-check"><input type="checkbox" checked={state.on} onChange={(e) => set((s) => ({ ...s, on: e.target.checked }))} />{label}</label>
}

function BulkEdit({ selected, onClose, onDone }) {
  const [price, setPrice] = useState({ on: false, value: '' })
  const [taxable, setTaxable] = useState({ on: false, value: 'yes' })
  const [rate, setRate] = useState({ on: false, value: '' })
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const apply = async () => {
    if (!price.on && !taxable.on && !rate.on) { setError('Tick at least one thing to change.'); return }
    if (price.on && !(num(price.value, -1) >= 0)) { setError('Enter a price of 0 or more.'); return }
    if (rate.on && rate.value.trim() !== '' && !(num(rate.value, -1) >= 0 && num(rate.value) <= 20)) { setError('The tax rate must be between 0 and 20%.'); return }
    setWorking(true)
    setError('')
    const failed = await forEach(selected, (item) => {
      const isTax = taxable.on ? taxable.value === 'yes' : Number(item.is_taxable ?? 1) === 1
      let taxRate = item.tax_rate != null && String(item.tax_rate) !== '' ? Number(item.tax_rate) : null
      if (!isTax) taxRate = null
      else if (rate.on) taxRate = rate.value.trim() === '' ? null : Number(rate.value)
      return createProduct({
        product_id: item.product_id, sku: item.sku || '', barcode: item.barcode || '', name: item.name || '',
        description: item.description || '', unit_cost: num(item.unit_cost), unit_price: price.on ? Number(price.value) : num(item.unit_price),
        is_taxable: isTax, tax_rate: taxRate,
      })
    })
    setWorking(false)
    if (failed) { setError(`${failed} of ${selected.length} weren’t updated.`); return }
    onDone('Updated')
    onClose()
  }
  return (
    <Modal open onClose={onClose} title="Price and tax" subtitle={`For ${selected.length} selected. Only what you tick changes.`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button><Button variant="primary" onClick={apply} loading={working}>Apply</Button></>}>
      {error && <ErrorBanner message={error} />}
      <div className="mf-modal-rows">
        <Check state={price} set={setPrice} label="Selling price" />
        {price.on && <input value={price.value} onChange={(e) => setPrice((s) => ({ ...s, value: e.target.value }))} inputMode="decimal" placeholder="0.00" aria-label="Selling price" />}
        <Check state={taxable} set={setTaxable} label="Taxable" />
        {taxable.on && (
          <select value={taxable.value} onChange={(e) => setTaxable((s) => ({ ...s, value: e.target.value }))} aria-label="Taxable">
            <option value="yes">Taxable</option>
            <option value="no">Not taxable</option>
          </select>
        )}
        <Check state={rate} set={setRate} label="Tax rate" />
        {rate.on && <input value={rate.value} onChange={(e) => setRate((s) => ({ ...s, value: e.target.value }))} inputMode="decimal" placeholder="Blank uses the location’s rate" aria-label="Tax rate" />}
      </div>
    </Modal>
  )
}
